import { useState } from "react";
import { G } from "@/brand";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, MessageSquarePlus } from "lucide-react";

const CATEGORIES = [
  { id: "prompt_quality", label: "Prompt quality" },
  { id: "missing_genre", label: "Missing genre / style" },
  { id: "editing_idea", label: "Editing idea" },
  { id: "bug", label: "Bug / confusing UX" },
  { id: "feature", label: "Feature request" },
];

export function FeedbackModal({ open, onClose }) {
  const { requireAuth } = useAuth();
  const [category, setCategory] = useState("prompt_quality");
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!requireAuth("Sign in to send account-linked feedback.")) { onClose(); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/feedback", { category, message });
      setDone(true);
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const close = () => { setDone(false); setMessage(""); setError(""); onClose(); };

  return (
    <div data-testid="feedback-modal" onClick={close} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...box, position: "relative" }}>
        <button onClick={close} style={closeBtn}>×</button>
        <div className="mono" style={{ fontSize: 9, letterSpacing: "0.5em", color: G.gold, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <MessageSquarePlus size={13} /> SUGGEST AN IMPROVEMENT
        </div>
        {done ? (
          <div className="mono" style={{ color: G.gold, fontSize: 12, letterSpacing: ".15em", textAlign: "center", padding: "24px 0" }}>
            ✦ THANK YOU — RECEIVED ✦
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {CATEGORIES.map((c) => (
                <button key={c.id} data-testid={`feedback-cat-${c.id}`} onClick={() => setCategory(c.id)} className="mono"
                  style={{ padding: "7px 12px", fontSize: 9, letterSpacing: ".08em", cursor: "pointer", textTransform: "uppercase",
                    background: category === c.id ? G.gold : "transparent", color: category === c.id ? G.black : G.muted,
                    border: `1px solid ${category === c.id ? G.gold : G.border}` }}>
                  {c.label}
                </button>
              ))}
            </div>
            <textarea data-testid="feedback-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
              placeholder="Tell us what would make Prompt Savant better…"
              style={{ width: "100%", padding: 14, background: G.panel, border: `1px solid ${G.border}`, color: G.text, fontSize: 14, fontFamily: "'Cormorant Garamond',serif", resize: "vertical" }} />
            {error && <div className="mono" style={{ color: "#ff5555", fontSize: 11, marginTop: 8 }}>{error}</div>}
            <button data-testid="feedback-submit" onClick={submit} disabled={loading || !message} className="btn-gold" style={{ width: "100%", padding: 13, fontSize: 10, marginTop: 14 }}>
              {loading ? "Sending…" : "Submit Suggestion"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const box = { background: G.dark, border: `1px solid ${G.borderHot}`, padding: "40px 36px", maxWidth: 480, width: "100%" };
const closeBtn = { position: "absolute", top: 14, right: 20, background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 24, lineHeight: 1 };
